import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { getPersonConnections, getPersonDetails, getPersonFinancialFootprint } from '../services/apiClient'
import { PersonConnection, PersonDetails, PersonFinancialFootprint as PersonFinancialFootprintData } from '../types'
import { Container, CountryFlagIcon, Section } from '../components/ui'
import type { TabDef } from '../components/ui'
import { Tabs } from '../components/ui'
import WatchlistControl from '../components/WatchlistControl'
import {
  PersonOverviewStrip,
  PersonConnectionsList,
  PersonAffiliationsView,
  PersonActivity,
  PersonFinancialFootprint,
  groupByCompany,
} from '../components/profile/person'
import ProfileActionLinks from '../components/profile/ProfileActionLinks'
import { dataCvrPersonUrl } from '../utils/cvrRegisterLinks'
import {
  detectCountry,
  formatAddressCountryLabel,
  formatProtectedAddressNote,
  stripTrailingCountryFromAddress,
} from '../utils/format'
import { navigateBackOrHome } from '../utils/appNavigation'
import { usePageTitle } from '../hooks/usePageTitle'
import { collectAllPersonActivity } from '../utils/personRecentActivity'

/**
 * Person profile — tabbed counterpart to CompanyProfilePage.
 *
 * Tabs:
 *   Overblik     — role breakdown + recent activity strip, active affiliations
 *   Forbindelser — co-participants in shared active companies (lazy-loaded)
 *   Aktivitet    — full activity history grouped by year
 *   Tidl. roller — historic affiliations
 */

// ── tab setup ────────────────────────────────────────────────────────────────

type TabId = 'overblik' | 'forbindelser' | 'aktivitet' | 'historik'
const TAB_IDS: TabId[] = ['overblik', 'forbindelser', 'aktivitet', 'historik']

function readTab(raw: string | null): TabId {
  if (raw === 'aktive') return 'overblik'
  return (TAB_IDS as string[]).includes(raw ?? '') ? (raw as TabId) : 'overblik'
}

// ── page ─────────────────────────────────────────────────────────────────────

const PersonDetailPage: React.FC = () => {
  const { enhedsNummer } = useParams<{ enhedsNummer: string }>()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const [person, setPerson]   = useState<PersonDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)

  const [connections, setConnections]           = useState<PersonConnection[]>([])
  const [connLoading, setConnLoading]           = useState(false)
  const [connError, setConnError]               = useState<string | null>(null)
  // Use a ref so the guard doesn't appear in the effect dependency array
  // (which would cause the cleanup to cancel the in-flight request).
  const connFetchedRef                          = useRef(false)

  const [footprint, setFootprint]               = useState<PersonFinancialFootprintData | null>(null)
  const [footprintLoading, setFootprintLoading] = useState(true)

  const activeTab: TabId = readTab(searchParams.get('tab'))

  const handleTabChange = (id: string) => {
    const next = readTab(id)
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next === 'overblik') {
          params.delete('tab')
        } else {
          params.set('tab', next)
        }
        return params
      },
      { replace: true }
    )
  }

  // ── person fetch ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enhedsNummer) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setPerson(null)
    connFetchedRef.current = false
    setConnections([])
    setConnError(null)
    getPersonDetails(enhedsNummer)
      .then((details) => { if (!cancelled) setPerson(details) })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Kunne ikke hente personoplysninger.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [enhedsNummer])

  // ── financial footprint — independent, non-blocking ────────────────────────
  useEffect(() => {
    if (!enhedsNummer) return
    let cancelled = false
    setFootprintLoading(true)
    setFootprint(null)
    getPersonFinancialFootprint(enhedsNummer)
      .then((data) => { if (!cancelled) setFootprint(data) })
      .catch(() => { if (!cancelled) setFootprint(null) })
      .finally(() => { if (!cancelled) setFootprintLoading(false) })
    return () => { cancelled = true }
  }, [enhedsNummer])

  // ── connections fetch — lazy, triggered when the tab is first selected ───────
  useEffect(() => {
    if (activeTab !== 'forbindelser' || connFetchedRef.current || !enhedsNummer) return
    connFetchedRef.current = true
    let cancelled = false
    setConnLoading(true)
    setConnError(null)
    getPersonConnections(enhedsNummer)
      .then((data) => { if (!cancelled) setConnections(data) })
      .catch((err) => {
        if (!cancelled) setConnError(err?.message || 'Kunne ikke hente forbindelser.')
      })
      .finally(() => { if (!cancelled) setConnLoading(false) })
    return () => { cancelled = true }
  }, [activeTab, enhedsNummer])

  const activeEntries   = useMemo(() => groupByCompany(person?.affiliations ?? [], true),  [person])
  const historicEntries = useMemo(() => groupByCompany(person?.affiliations ?? [], false), [person])
  const activeCount   = activeEntries.reduce((n, e) => n + e.roles.length, 0)
  const historicCount = historicEntries.reduce((n, e) => n + e.roles.length, 0)
  const activityCount = useMemo(
    () => (person ? collectAllPersonActivity(person.affiliations).length : 0),
    [person]
  )

  const pageTitle = useMemo(() => {
    if (loading) return 'Person'
    if (error || !person) return 'Person ikke fundet'
    return person.navn || `Person ${person.enhedsNummer}`
  }, [loading, error, person])
  usePageTitle(pageTitle)

  // ── loading / error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Container width="prose" className="py-16">
        <p className="font-mono text-xs uppercase tracking-widest text-ink-500">
          Henter personoplysninger…
        </p>
      </Container>
    )
  }

  if (error || !person) {
    return (
      <Container width="prose" className="py-16">
        <BackLink />
        <h1 className="font-serif text-2xl text-ink mt-6">Kunne ikke vise personen</h1>
        <p className="mt-3 text-sm text-signal-900 bg-signal-50 border border-signal-200 rounded-sm px-3 py-2">
          {error || 'Personen blev ikke fundet.'}
        </p>
      </Container>
    )
  }

  // ── derived values ─────────────────────────────────────────────────────────
  const country = detectCountry(person.adresse)
  const enhedsStr = String(person.enhedsNummer ?? '').trim()
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${location.pathname}${location.search}`
      : `${location.pathname}${location.search}`
  const datacvrHref = dataCvrPersonUrl(person.enhedsNummer)

  const tabs: TabDef[] = [
    { id: 'overblik',      label: 'Overblik' },
    { id: 'forbindelser',  label: 'Forbindelser' },
    {
      id: 'aktivitet',
      label: 'Aktivitet',
      count: activityCount > 0 ? activityCount : null,
    },
    {
      id: 'historik',
      label: 'Tidl. roller',
      count: historicCount > 0 ? historicCount : null,
    },
  ]

  return (
    <Container width="prose" className="py-10">
      <BackLink />

      {/* ── Identity header ─────────────────────────────────────────────── */}
      <header className="mt-6 pb-4" data-testid="person-profile-head">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm text-ink-500 mb-2">
          <span className="font-mono text-xs uppercase tracking-widest text-signal">Person</span>
          <span className="font-mono inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>#{enhedsStr || '—'}</span>
            <ProfileActionLinks
              showCopyId={false}
              shareUrl={shareUrl}
              datacvrHref={datacvrHref}
              datacvrAriaLabel="Åbn person på DataCVR i nyt vindue"
            />
          </span>
        </div>

        <div
          className="flex items-start justify-between gap-x-4 gap-y-2"
          data-testid="profile-title-row"
        >
          <h1 className="font-serif text-3xl sm:text-4xl text-ink leading-tight min-w-0 flex-1 inline-flex items-baseline gap-2 flex-wrap">
            {person.navn || <em className="text-ink-400">Uden navn</em>}
            {country.isForeign && country.countryCode && (
              <CountryFlagIcon code={country.countryCode} className="text-2xl sm:text-3xl" />
            )}
          </h1>
          <WatchlistControl
            iconOnly
            iconSize="lg"
            entityType="person"
            entityId={String(person.enhedsNummer)}
            displayName={person.navn || `Person ${person.enhedsNummer}`}
            countryCode={country.isForeign ? country.countryCode : null}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-ink-700">
          {person.isAddressConfidential ? (
            <span>{formatProtectedAddressNote(person.adresse)}</span>
          ) : (
            person.adresse && (
              <span>
                {stripTrailingCountryFromAddress(
                  formatAddressCountryLabel(person.adresse),
                  country.isForeign ? country.countryCode : undefined
                )}
              </span>
            )
          )}
        </div>

        <PersonFinancialFootprint
          footprint={footprint}
          loading={footprintLoading}
          personName={person.navn || `Person ${person.enhedsNummer}`}
        />

        <Tabs
          dense
          tabs={tabs}
          active={activeTab}
          onChange={handleTabChange}
          ariaLabel="Personsektioner"
          className="mt-4"
        />
      </header>

      {/* ── Overblik ──────────────────────────────────────────────────────── */}
      <TabPanel id="overblik" active={activeTab}>
        <div className="space-y-6">
          <PersonOverviewStrip affiliations={person.affiliations} />

          {activeCount > 0 ? (
            <PersonAffiliationsView
              entries={activeEntries}
              sectionTitle="Nuværende virksomhedsroller"
              sectionDescription="Selskaber personen er aktivt registreret i."
              borderTop={false}
            />
          ) : (
            <p className="text-sm text-ink-500">Ingen aktive roller registreret.</p>
          )}
        </div>
      </TabPanel>

      {/* ── Forbindelser ──────────────────────────────────────────────────── */}
      <TabPanel id="forbindelser" active={activeTab}>
        <Section
          title="Forretningsforbindelser"
          description="Andre registrerede deltagere i de samme aktive selskaber, rangeret efter antal fælles selskaber og samlet overlappende periode."
        >
          <PersonConnectionsList
            connections={connections}
            loading={connLoading}
            error={connError}
          />
        </Section>
      </TabPanel>

      {/* ── Aktivitet ─────────────────────────────────────────────────────── */}
      <TabPanel id="aktivitet" active={activeTab}>
        <PersonActivity affiliations={person.affiliations} />
      </TabPanel>

      {/* ── Tidl. roller ──────────────────────────────────────────────────── */}
      <TabPanel id="historik" active={activeTab}>
        {historicCount > 0 ? (
          <PersonAffiliationsView
            entries={historicEntries}
            variant="historic"
            sectionTitle={`Tidligere roller (${historicCount})`}
            sectionDescription="Selskaber personen tidligere var registreret i."
          />
        ) : (
          <p className="mt-6 text-sm text-ink-500">Ingen tidligere roller registreret.</p>
        )}
      </TabPanel>
    </Container>
  )
}

// ── sub-components ────────────────────────────────────────────────────────────

interface TabPanelProps {
  id: TabId
  active: TabId
  children: React.ReactNode
}

const TabPanel: React.FC<TabPanelProps> = ({ id, active, children }) => {
  const hidden = id !== active
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${id}`}
      aria-labelledby={`tab-${id}`}
      hidden={hidden}
      className={hidden ? '' : 'mt-6'}
    >
      {!hidden && children}
    </div>
  )
}

const BackLink: React.FC = () => {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigateBackOrHome(navigate)}
      className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink bg-transparent border-0 p-0 cursor-pointer font-sans text-left"
    >
      <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
      Tilbage
    </button>
  )
}

export default PersonDetailPage
